import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import QRCode from "react-qr-code";

export default function InventoryAssignmentsPage() {
  const navigate = useNavigate();
  usePageTitle("Inventory");

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  const [resultData, setResultData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [openReceiveQR, setOpenReceiveQR] = useState(false);
  const isSupervisor = assignments.some((row) => row.role === "SUPERVISOR");
  const [qrCodeJson, setQrCodeJson] = useState(null);

  const handleClose = () => setOpen(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get("/agents/inventories.json")
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setResultData(result.data);
            setAssignments(result.data.data.assignments || []);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch((e) => {
          console.log(e);
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openReceiveQR} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Receive Stock</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox
            py={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <MDBox
              p={2}
              borderRadius="lg"
              bgcolor="white"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width={240}
              height={240}
            >
              <QRCode
                value={qrCodeJson}
                size={220}
                style={{
                  height: "auto",
                  maxWidth: "100%",
                  width: "100%",
                }}
              />
            </MDBox>

            <MDBox mt={3} textAlign="center">
              <MDTypography variant="h6" fontWeight="medium">
                Ask your supervisor to scan this code
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenReceiveQR(false);
              setRefreshPage(!refreshPage);
            }}
            color="dark"
            variant="outlined"
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && (
        <MDBox mt={1} mb={5}>
          <MDBox mb={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              px={1}
              alignItems="center"
            >
              <MDBox pt={0.5}>
                <Icon>inventory</Icon>
              </MDBox>

              <MDBox>
                <MDTypography variant="h6" fontWeight="bold">
                  Inventories
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox mt={0}>
              <Divider />
            </MDBox>
          </MDBox>

          {!isSupervisor && (
            <Card sx={{ marginTop: 0, mb: 2 }}>
              <MDBox p={2}>
                <MDBox display="flex" alignItems="center" gap={2}>
                  <MDBox
                    width={44}
                    height={44}
                    borderRadius="lg"
                    bgcolor="dark.main"
                    display="flex"
                    alignItems="top"
                    justifyContent="center"
                  >
                    <Icon sx={{ color: "white" }}>qr_code_2</Icon>
                  </MDBox>

                  <MDBox flex={1}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Receive Stock
                    </MDTypography>

                    <MDTypography
                      variant="body2"
                      color="text"
                      fontWeight="light"
                      fontSize={12}
                    >
                      Generate a QR code for your supervisor to transfer stock
                      to you.
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox mt={2}>
                  <MDButton
                    fullWidth
                    color="dark"
                    variant="contained"
                    startIcon={<Icon>qr_code_2</Icon>}
                    onClick={() => {
                      const qrPayload = JSON.stringify({
                        a: resultData?.data?.reference,
                        t: "I",
                      });
                      setQrCodeJson(qrPayload);
                      setOpenReceiveQR(true);
                    }}
                  >
                    Show QR Code
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          )}

          {assignments.length === 0 && (
            <Card sx={{ marginTop: 0 }}>
              <MDBox p={4} textAlign="center">
                <MDTypography variant="h3" color="text">
                  <Icon fontSize="large">inventory_2</Icon>
                </MDTypography>

                <MDTypography variant="h6" fontWeight="medium" mt={1}>
                  No inventory assigned
                </MDTypography>

                <MDTypography variant="button" color="text" fontWeight="light">
                  You do not currently have inventory in any tracker.
                </MDTypography>
              </MDBox>
            </Card>
          )}

          <MDBox display="flex" flexDirection="column" gap={2}>
            {assignments.map((row) => (
              <Card
                key={`${row.account.reference}-${row.tracker.reference}`}
                sx={{ marginTop: 0, cursor: "pointer" }}
                onClick={() =>
                  navigate(`/inventories/${row.tracker.reference}`)
                }
              >
                <MDBox p={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <MDBox flex={1}>
                      <MDTypography variant="h6" fontWeight="medium">
                        {row.tracker.title}
                      </MDTypography>

                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="light"
                      >
                        {row.account.title}
                      </MDTypography>
                    </MDBox>

                    <Chip
                      label={row.role === "SUPERVISOR" ? "Supervisor" : "Agent"}
                      color={row.role === "SUPERVISOR" ? "info" : "default"}
                      size="small"
                    />
                  </MDBox>

                  <MDBox
                    mt={3}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography
                      variant="button"
                      color="primary"
                      fontWeight="medium"
                    >
                      Open inventory
                    </MDTypography>

                    <Icon color="primary">chevron_right</Icon>
                  </MDBox>
                </MDBox>
              </Card>
            ))}
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
